import { gettext } from '@adjust/dashboard-core/lib/utils/localization';
import {
  audienceParser,
  settingsParser,
  userParser,
} from '@react/constants/historyEvent/preparation/prepareIconParsers';
import { registerIcons } from '@react/constants/historyEvent/preparation/prepareIcons';
import { registerKindLocalization } from '@react/constants/historyEvent/localization/kindLocalization';
import { registerMessageLocalizations } from '@react/constants/historyEvent/localization/messageLocalization';
import { registerPrepareNames } from '@react/constants/historyEvent/preparation/prepareNames';
import { uninstallPrepareNameParser } from '@react/constants/historyEvent/preparation/prepareNameParsers/uninstallPrepareNameParser';
import { registerPrepareActions } from '@react/constants/historyEvent/preparation/prepareAction';
import { uninstallPrepareActionParser } from '@react/constants/historyEvent/preparation/prepareActionParsers/uninstallPrepareActionParser';
import { registerActionLocalization } from '@react/constants/historyEvent/localization/actionLocalization';
import { registerParamsUpdater } from '@react/constants/historyEvent/preparation/paramsUpdater';
import { defaultUpdater } from '@react/constants/historyEvent/preparation/paramsUpdaters/defaultUpdater';
import { partnerNameUpdater } from '@react/constants/historyEvent/preparation/paramsUpdaters/partnerNameUpdater';
import { audienceBuilderExportUpdater } from '@react/constants/historyEvent/preparation/paramsUpdaters/audienceBuilderExportUpdater';
import { appMigrationUpdater } from '@react/constants/historyEvent/preparation/paramsUpdaters/appMigrationUpdater';
import { snapchatCredentialsUpdater } from '@react/constants/historyEvent/preparation/paramsUpdaters/snapchatCredentialsUpdater';
import { partnerSetupPausedActionParser } from './preparation/prepareActionParsers/partnerSetupPausedActionParser';
import { partnerSetupActivatedActionParser } from './preparation/prepareActionParsers/partnerSetupActivatedActionParser';

function registerPreparation() {
  registerPrepareNames(register => {
    register(uninstallPrepareNameParser);
  });

  registerPrepareActions(register => {
    register(uninstallPrepareActionParser);
    register(partnerSetupPausedActionParser);
    register(partnerSetupActivatedActionParser);
  });

  registerParamsUpdater(register => {
    register(defaultUpdater);
    register(partnerNameUpdater);
    register(audienceBuilderExportUpdater);
    register(appMigrationUpdater);
    register(snapchatCredentialsUpdater);
  });

  registerIcons(register => {
    register('icon-users', audienceParser);
    register('icon-user', userParser);
    register('icon-cog', settingsParser);
  });
}

function registerLocalization() {
  registerActionLocalization(register => {
    register('changed access', gettext('account-history.activity.changed-access'));
    register('linked', gettext('account-history.activity.linked'));
    register('unlinked', gettext('account-history.activity.unlinked'));
    register('refreshed', gettext('account-history.activity.reset'));
    register('deleted', gettext('account-history.activity.deleted'));
    register('created', gettext('account-history.activity.created'));
    register('enabled', gettext('account-history.activity.enabled'));
    register('disabled', gettext('account-history.activity.disabled'));
    register('changed', gettext('account-history.activity.changed'));
    register('invited', gettext('account-history.activity.invited'));
    register('restored', gettext('account-history.activity.restored'));
    register('archived', gettext('account-history.activity.archived'));
    register('unarchived', gettext('account-history.activity.unarchived'));
    register('cancelled', gettext('account-history.activity.cancelled'));
    register('deactivated', gettext('account-history.activity.deactivated'));
    register('reactivated', gettext('account-history.activity.reactivated'));
    register('removed', gettext('account-history.activity.removed'));
    register('uploaded', gettext('account-history.activity.uploaded'));
    register('added', gettext('account-history.activity.added'));
    register('accepted', gettext('account-history.activity.accepted'));
    register('paused', gettext('account-history.activity.paused'));
    register('activated', gettext('account-history.activity.activated'));
  });

  registerKindLocalization(register => {
    register('app', gettext('account-history.activity.app'));
    register('user', gettext('account-history.activity.user'));
    register('account', gettext('account-history.activity.account'));
    register('secret', gettext('account-history.activity.secret'));
    register('audience', gettext('account-history.activity.audience'));
    register('package', gettext('account-history.settings.package'));
    register('configuration', gettext('account-history.settings.configuration'));
  });

  registerMessageLocalizations(register => {
    // dashboard:mobile_app
    register('dashboard:mobile_app:created', '{{ name }}');
    register('dashboard:mobile_app:deleted', '{{ name }}');
    register('dashboard:mobile_app:restored', '{{ name }}');
    register('dashboard:mobile_app:changed_platform_settings', gettext('account-history.event.platform-settings'));
    register(
      'dashboard:mobile_app:changed_and_outdated_platform_settings',
      gettext('account-history.event.partner-settings-reset'),
    );
    register('dashboard:mobile_app:changed_partner_settings', gettext('account-history.event.partner-settings'));
    register('dashboard:mobile_app:changed_parameter_mapping', gettext('account-history.event.parameter-mapping'));
    register('dashboard:mobile_app:deleted_partner_settings', gettext('account-history.event.partner-settings'));
    register(
      'dashboard:mobile_app:partner_settings_batch_destroyed',
      gettext('account-history.event.partner-settings'),
    );
    register('dashboard:mobile_app:partner_module_active', gettext('account-history.event.partner-setup.activated'));
    register('dashboard:mobile_app:partner_module_paused', gettext('account-history.event.partner-setup.paused'));
    register(
      'dashboard:mobile_app:changed_google_conversion_id',
      gettext('account-history.event.google-conversion-id'),
    );
    register(
      'dashboard:mobile_app:changed_attribution_settings',
      gettext('account-history.event.attribution-settings'),
    );
    register(
      'dashboard:mobile_app:changed_fraud_prevention_settings',
      gettext('account-history.event.fraud-prevention-settings'),
    );
    register(
      'dashboard:mobile_app:changed_purchase_verification_settings',
      gettext('account-history.event.purchase-verification-settings'),
    );
    register('dashboard:mobile_app:changed_callback_url', gettext('account-history.event.callback-url'));
    register('dashboard:mobile_app:changed_conversion_id', gettext('account-history.event.partner-conversion-id'));
    register('dashboard:mobile_app:changed_raw_export_settings', gettext('account-history.event.raw-export-settings'));
    register('dashboard:mobile_app:migrated_to', gettext('account-history.event.mobile-app-to-account-transfer'));
    register('dashboard:mobile_app:migrated_from', gettext('account-history.event.mobile-app-from-account-transfer'));

    register('dashboard:mobile_app:created_report_mailing', gettext('account-history.event.email-report'));
    register('dashboard:mobile_app:updated_report_mailing', gettext('account-history.event.email-report'));

    register(
      'dashboard:mobile_app:sdk_signature_check_enabled',
      gettext('account-history.event.max-app-security-enabled'),
    );
    register(
      'dashboard:mobile_app:sdk_signature_check_disabled',
      gettext('account-history.event.max-app-security-disabled'),
    );
    register('dashboard:mobile_app:created_secret', gettext('account-history.event.secret-for-app'));
    register('dashboard:mobile_app:updated_secret', gettext('account-history.event.secret-for-app'));
    register('dashboard:mobile_app:deleted_secret', gettext('account-history.event.secret-for-app'));
    register('dashboard:mobile_app:deactivated_secret', gettext('account-history.event.secret-for-app'));
    register('dashboard:mobile_app:reactivated_secret', gettext('account-history.event.secret-for-app'));
    register(
      'dashboard:mobile_apps/app_id_verification:requested',
      gettext('account-history.event.app-id-verification.requested'),
    );
    register('dashboard:mobile_apps/app_id:verified', gettext('account-history.event.app-id-verification.verified'));

    // dashboard:event_type
    register('dashboard:event_type:created', gettext('account-history.event.event-type-for-app'));
    register('dashboard:event_type:archived', gettext('account-history.event.event-type-for-app'));
    register('dashboard:event_type:unarchived', gettext('account-history.event.event-type-for-app'));
    register('dashboard:event_type:updated', gettext('account-history.event.event-type-for-app'));

    // dashboard:tracker
    register('dashboard:tracker:created', gettext('account-history.event.tracker-for-app'));
    register('dashboard:tracker:archived', gettext('account-history.event.tracker-for-app'));
    register('dashboard:tracker:unarchived', gettext('account-history.event.tracker-for-app'));
    register('dashboard:tracker:updated', gettext('account-history.event.tracker-of-app'));

    // dashboard:twitter_setting
    register('dashboard:twitter_setting:created', gettext('account-history.event.twitter-handle'));
    register('dashboard:twitter_setting:deleted', gettext('account-history.event.twitter-handle'));

    // dashboard:iron_source_credential
    register('dashboard:iron_source_credential:created', gettext('account-history.event.ironsource-handle'));
    register('dashboard:iron_source_credential:deleted', gettext('account-history.event.ironsource-handle'));
    register('dashboard:iron_source_credential:updated', gettext('account-history.event.ironsource-handle'));
    register('oauth_service:iron_source_credential:created', gettext('account-history.event.ironsource-handle'));
    register('oauth_service:iron_source_credential:deleted', gettext('account-history.event.ironsource-handle'));
    register('oauth_service:iron_source_credential:updated', gettext('account-history.event.ironsource-handle'));

    // dashboard:twitter_ads/account
    register('dashboard:twitter_ads/account:enable', gettext('account-history.event.twitter-handle'));
    register('dashboard:twitter_ads/account:disable', gettext('account-history.event.twitter-handle'));

    // dashboard:facebook_credential
    register('dashboard:facebook_credential:created', gettext('account-history.event.facebook-handle'));
    register('dashboard:facebook_credential:deleted', gettext('account-history.event.facebook-handle'));
    register('oauth_service:facebook_credential:created', gettext('account-history.event.facebook-handle'));
    register('oauth_service:facebook_credential:deleted', gettext('account-history.event.facebook-handle'));

    // dashboard:facebook_ads/account
    register('dashboard:facebook_ads/account:enabled', gettext('account-history.event.facebook-handle'));
    register('dashboard:facebook_ads/account:disabled', gettext('account-history.event.facebook-handle'));
    register('oauth_service:facebook_ads/account:enabled', gettext('account-history.event.facebook-handle'));
    register('oauth_service:facebook_ads/account:disabled', gettext('account-history.event.facebook-handle'));

    // dashboard:google_credential
    register('dashboard:google_credential:created', gettext('account-history.event.google-account'));
    register('dashboard:google_credential:deleted', gettext('account-history.event.google-account'));
    register('oauth_service:google_credential:created', gettext('account-history.event.google-account'));
    register('oauth_service:google_credential:deleted', gettext('account-history.event.google-account'));
    register('oauth_service:google_credential:updated', gettext('account-history.event.google-account'));

    // dashboard:google/customer_account
    register('dashboard:google/customer_account:enabled', gettext('account-history.event.google-customer-account'));
    register('dashboard:google/customer_account:disabled', gettext('account-history.event.google-customer-account'));
    register('oauth_service:google/customer_account:enabled', gettext('account-history.event.google-customer-account'));
    register(
      'oauth_service:google/customer_account:disabled',
      gettext('account-history.event.google-customer-account'),
    );

    // dashboard:tencent_credential
    register('dashboard:tencent_credential:created', gettext('account-history.event.tencent-account'));
    register('dashboard:tencent_credential:deleted', gettext('account-history.event.tencent-account'));
    register('oauth_service:tencent_credential:created', gettext('account-history.event.auth-service.tencent-account'));
    register('oauth_service:tencent_credential:deleted', gettext('account-history.event.auth-service.tencent-account'));

    // accounts:user

    register('accounts:user:added_to_account', gettext('account-history.event.to-account-change'));
    register('accounts:user:removed_from_account', gettext('account-history.event.from-account-change'));
    register('accounts:user:invited', gettext('account-history.event.to-account-change'));
    register('accounts:user:changed_main_account', gettext('account-history.event.from-account-change'));
    register('accounts:user:deleted', gettext('account-history.event.from-account-change'));

    // accounts:users/authorized
    register('accounts:users/authorized:changed_access', gettext('account-history.event.user-permissions'));

    // accounts:payment_plan
    register('accounts:payment_plan:updated', gettext('account-history.event.payment-package-change'));
    register('accounts:payment_plan:cancelled', gettext('account-history.event.payment-package-switch'));

    // accounts:account
    register('accounts:account:updated', gettext('account-history.event.account-settings'));
    register('accounts:account:account_paused', gettext('account-history.event.account-paused'));
    register('accounts:account:account_suspended', gettext('account-history.event.account-suspended'));
    register('accounts:account:account_terminated', gettext('account-history.event.account-terminated'));
    register(
      'accounts:account:account_termination_cancelled',
      gettext('account-history.event.account-termination-canceled'),
    );
    register('accounts:account:account_reactivated', gettext('account-history.event.account-reactivated'));
    register(
      'accounts:account:attribution_privacy_model_changed',
      gettext('account-history.attribution-privacy-model-changed'),
    );

    // audience_builder:segment

    register('audience_builder:segment:created', '{{ name }}');
    register('audience_builder:segment:updated', '{{ name }}');
    register('audience_builder:segment:deleted', '{{ name }}');

    // audience_builder:export
    register('audience_builder:export:updated', gettext('account-history.activity.group-updated'));
    register('audience_builder:export:created', gettext('account-history.event.group-linked-to'));
    register('audience_builder:export:deleted', gettext('account-history.event.group-unlinked-from'));

    // audience_builder:segment/group

    register('audience_builder:segment/group:refreshed', gettext('account-history.event.group-link-reset'));

    // dashboard:snapchat_ad_account
    register('dashboard:snapchat_ad_account:removed', gettext('account-history.event.snapchat-handle'));
    register('dashboard:snapchat_ad_account:disabled', gettext('account-history.event.snapchat-handle'));
    register('dashboard:snapchat_ad_account:enabled', gettext('account-history.event.snapchat-handle'));
    register('dashboard:snapchat_ad_account:added', gettext('account-history.event.snapchat-handle'));

    register('oauth_service:snapchat_ads/account:removed', gettext('account-history.event.snapchat-handle'));
    register('oauth_service:snapchat_ads/account:disabled', gettext('account-history.event.snapchat-handle'));
    register('oauth_service:snapchat_ads/account:enabled', gettext('account-history.event.snapchat-handle'));
    register('oauth_service:snapchat_ads/account:added', gettext('account-history.event.snapchat-handle'));

    // dashboard:snapchat_credential
    register('dashboard:snapchat_credential:deleted', gettext('account-history.event.snapchat-account'));
    register('dashboard:snapchat_credential:updated', gettext('account-history.event.snapchat-account'));
    register('dashboard:snapchat_credential:added', gettext('account-history.event.snapchat-account'));

    register('oauth_service:snapchat_credential:deleted', gettext('account-history.event.snapchat-account'));
    register('oauth_service:snapchat_credential:updated', gettext('account-history.event.snapchat-account'));
    register('oauth_service:snapchat_credential:added', gettext('account-history.event.snapchat-account'));

    // oauth_service:apple_search/credential
    register('oauth_service:apple_search/credential:created', gettext('account-history.event.apple-search-handle'));
    register('oauth_service:apple_search/credential:deleted', gettext('account-history.event.apple-search-handle'));
    register('oauth_service:apple_search/credential:updated', gettext('account-history.event.apple-search-handle'));

    // oauth_service:apple_search/ads_account
    register('oauth_service:apple_search/ads_account:enabled', gettext('account-history.event.apple-search-handle'));
    register('oauth_service:apple_search/ads_account:disabled', gettext('account-history.event.apple-search-handle'));

    // oauth_service:applovin/credential
    register('oauth_service:applovin/credential:created', gettext('account-history.event.applovin-handle'));
    register('oauth_service:applovin/credential:deleted', gettext('account-history.event.applovin-handle'));
    register('oauth_service:applovin/credential:updated', gettext('account-history.event.applovin-handle'));

    // dashboard:mobile_apps/uninstall_settings
    register(
      'dashboard:mobile_apps/uninstall_settings:created@itunes',
      gettext('account-history.event.apn-certificate-file'),
    );
    register(
      'dashboard:mobile_apps/uninstall_settings:updated@itunes',
      gettext('account-history.event.apn-certificate-file'),
    );
    register(
      'dashboard:mobile_apps/uninstall_settings:deleted@itunes',
      gettext('account-history.event.apn-certificate-file'),
    );
    register('dashboard:mobile_apps/uninstall_settings:created@google', gettext('account-history.event.fcm-key'));
    register('dashboard:mobile_apps/uninstall_settings:updated@google', gettext('account-history.event.fcm-key'));
    register('dashboard:mobile_apps/uninstall_settings:deleted@google', gettext('account-history.event.fcm-key'));
    register('dashboard:mobile_apps/subdomain:updated', gettext('account-history.event.subdomain-updated'));

    // privacy model
    register(
      'dashboard:mobile_apps/attribution_privacy_model:updated',
      gettext('account-history.event.attribution-privacy-model.updated'),
    );

    // accounts:terms_confirmed
    register('accounts:terms_confirmed:audit', gettext('account-history.event.terms-and-conditions-update-for-term'));
    register('accounts:terms_confirmed:sign_up_audit', gettext('account-history.event.terms-and-conditions-update'));
    register('accounts:terms_confirmed:audit_for_app', gettext('account-history.app-terms.confirmed'));

    // dashboard:imei
    register('dashboard:imei:changed', gettext('account-history.imei.changed'));

    // dashboard:mobile_apps/preview_link_settings
    register(
      'dashboard:mobile_apps/preview_link_settings:created',
      gettext('account-history.event.social-sharing-preview'),
    );
    register(
      'dashboard:mobile_apps/preview_link_settings:updated',
      gettext('account-history.event.social-sharing-preview'),
    );

    // accounts:feature
    // Package upgrade/downgrade for payment plan accounts:
    register('accounts:feature:upgrade', '{{ feature }}');
    register('accounts:feature:downgrade', '{{ feature }}');

    // accounts:featurama
    register('accounts:featurama:upgrade', '{{ feature }}');
    register('accounts:featurama:downgrade', '{{ feature }}');

    // package:sso
    register('package:sso:added', '{{ feature }}');

    // accounts:saml_config
    register('accounts:saml_config:added', gettext('account-history.description.sso-added'));
    register('accounts:saml_config:changed', gettext('account-history.description.sso-changed'));

    // accounts:account:two_factor_auth
    register('accounts:account:two_factor_auth_enabled', gettext('account-history.event.2fa'));
    register('accounts:account:two_factor_auth_disabled', gettext('account-history.event.2fa'));

    // dashboard:s2s
    register('dashboard:s2s_authentication:changed', gettext('account-history.event.s2s-security.changed'));
    register('dashboard:authentication_token:created', gettext('account-history.event.s2s-token.created'));
    register('dashboard:authentication_token:deleted', gettext('account-history.event.s2s-token.deleted'));
    register('dashboard:authentication_token:updated', gettext('account-history.event.s2s-token.restored'));

    register('dashboard:ios14_migrator:migrated', gettext('account-history.event.ios14-migration'));
    register('dashboard:skadnetwork_settings:updated', gettext('account-history.event.skadnetwork_settings.updated'));

    register('oauth_service:tiktok_credential:created', gettext('account-history.event.tiktok-handle'));
    register('oauth_service:tiktok_credential:deleted', gettext('account-history.event.tiktok-handle'));
    register('oauth_service:tiktok_credential:updated', gettext('account-history.event.tiktok-handle'));

    // dashboard:subscription_credential
    register(
      'dashboard:subscription_credential:created',
      gettext('account-history.description.subscription-credential'),
    );
    register(
      'dashboard:subscription_credential:updated',
      gettext('account-history.description.subscription-credential'),
    );
    register(
      'dashboard:subscription_credential:deleted',
      gettext('account-history.description.subscription-credential'),
    );

    register('dashboard:data_privacy_setting:enabled', gettext('account-history.event.data-privacy'));
    register('dashboard:data_privacy_setting:disabled', gettext('account-history.event.data-privacy'));
    register('dashboard:data_privacy_setting:changed', gettext('account-history.event.data-privacy'));

    // oauth_service:apple_ads_campaign/credential
    register('oauth_service:apple_ads_campaign/credential:created', gettext('account-history.event.apple-ads-handle'));
    register('oauth_service:apple_ads_campaign/credential:deleted', gettext('account-history.event.apple-ads-handle'));
    register('oauth_service:apple_ads_campaign/credential:updated', gettext('account-history.event.apple-ads-handle'));

    // Credentials operation data
    register('credentials:network:created', gettext('account-history.event.network-created'));
    register('credentials:network:removed', gettext('account-history.event.network-removed'));
    register('credentials:network:changed', gettext('account-history.event.network-changed'));
    register('credentials:network:enabled', gettext('account-history.event.network-enabled'));
    register('credentials:network:disabled', gettext('account-history.event.network-disabled'));
  });
}

let isInitialized = false;

export async function initHistoryEvents(): Promise<true> {
  if (!isInitialized) {
    registerPreparation();
    registerLocalization();
    isInitialized = true;
  }
  return true;
}
